import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import { getOauthScopesOverview, getScopesSignInUrl } from 'src/services/auth';
import { ScopesOverviewResponse } from 'src/types/oAuth';
import { ApiResponse } from 'src/types/commonResponseTypes';

const EMPTY_SCOPES: string[] = [];

class OauthScopesStore {
  overview: ScopesOverviewResponse | null = null;
  isLoading = false;
  isSubmitting = false;
  selectedScopes: string[] = [...EMPTY_SCOPES];

  constructor() {
    makeAutoObservable(this);
  }

  get hasSelectionChanges(): boolean {
    if (!this.overview) {
      return this.selectedScopes.length > 0;
    }

    return !this.areScopesEqual(
      this.selectedScopes,
      this.overview.grantedScopes,
    );
  }

  private areScopesEqual(first: string[], second: string[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSet = new Set(first);

    return second.every((value) => firstSet.has(value));
  }

  private normalizeScopes(scopes: string[]): string[] {
    return Array.from(new Set(scopes));
  }

  get availableSelectedScopes(): string[] {
    if (!this.overview) {
      return [...this.selectedScopes];
    }

    const availableSet = new Set(this.overview.availableScopes);

    return this.selectedScopes.filter((scope) => availableSet.has(scope));
  }

  toggleScope = (scope: string) => {
    if (this.selectedScopes.includes(scope)) {
      this.selectedScopes = this.selectedScopes.filter(
        (value) => value !== scope,
      );
    } else {
      this.selectedScopes = [...this.selectedScopes, scope];
    }
  };

  resetSelection = () => {
    if (!this.overview) {
      this.selectedScopes = [...EMPTY_SCOPES];

      return;
    }

    this.selectedScopes = [...this.overview.grantedScopes];
  };

  fetchOverview = async (): Promise<ApiResponse<ScopesOverviewResponse>> => {
    this.isLoading = true;

    try {
      const response = await getOauthScopesOverview();
      const overviewResponse = response.content;

      if (response.success && overviewResponse) {
        runInAction(() => {
          this.overview = {
            ...overviewResponse,
            availableScopes: this.normalizeScopes(
              overviewResponse.availableScopes,
            ),
            grantedScopes: this.normalizeScopes(overviewResponse.grantedScopes),
            missingScopes: this.normalizeScopes(overviewResponse.missingScopes),
          };
          this.selectedScopes = [...this.overview.grantedScopes];
        });
      }

      return response;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  startScopesSignIn = async (
    redirectUrl?: string,
  ): Promise<ApiResponse<string>> => {
    this.isSubmitting = true;

    try {
      const response = await getScopesSignInUrl(
        this.availableSelectedScopes,
        redirectUrl,
      );

      return response;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };
}

export const [OauthScopesStoreProvider, useOauthScopesStore] =
  createStoreContext(OauthScopesStore);
